import Router, { getUrlWithUtmParams } from '@/components/common/router';
import { ROUTEMAP } from '@/components/fakeDoor/constants';
import { BookingType } from '@/components/fakeDoor/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { formatDate } from '@/utils/formatters';
import { IVR_DATA } from '@/constants';
import { appointmentCardFields } from '@/config/appointmentCardFields';
import { APPOINTMENT_CARD_TYPE } from '@/constants/enums';

// Define a generic type for the appointments object
type AppointmentsData = Record<string, any>;

interface CardDetails {
    ctaName: string;
    vehicleDetails?: {};
    paymentLink?: string;
    cardType?: string;
    navigatePath?: string;
    customerDetails?: {};
    cardText?: string;
    cardName?: string;
    booking?: any;
}

interface AppointmentsState {
    appointments: AppointmentsData;
    cardDetails: CardDetails | null;
}

const initialState: AppointmentsState = {
    appointments: {},
    cardDetails: null,
};

const appointmentsSlice = createSlice({
    name: 'appointments',
    initialState,
    reducers: {
        setAppointments: (state, action: PayloadAction<AppointmentsData>) => {
            state.appointments = action.payload;
            state.cardDetails = updateCardDetails(action.payload);
        },
        clearAppointments: (state) => {
            state.appointments = {};
            state.cardDetails = null;
        },
    },
});

function updateCardDetails(appointmentDetails: any): CardDetails | null {
    if (!appointmentDetails || Object.values(appointmentDetails).length === 0)
        return null;

    let cardType: APPOINTMENT_CARD_TYPE | undefined;
    const { status, booking_details, payment_details } = appointmentDetails;
    const appointment_fulfillment_type =
        booking_details?.appointment_fulfillment_type;

    if (['SCHEDULED', 'RESCHEDULED'].includes(status)) {
        cardType = APPOINTMENT_CARD_TYPE.VIEW_BOOKING;
    } else if (status === 'CREATED') {
        if (appointment_fulfillment_type === BookingType.PICK_AND_DROP) {
            cardType = APPOINTMENT_CARD_TYPE.RESUME_BOOKING;
        } else if (
            appointment_fulfillment_type === BookingType.SLOT_BOOKING ||
            appointment_fulfillment_type === BookingType.SELF_DROP
        ) {
            cardType = APPOINTMENT_CARD_TYPE.SELECT_SLOT;
        }
    } else if (
        status === 'PICKUP_ADDRESS_ADDED' ||
        status === 'PAYMENT_CONFIRMED' ||
        status === 'SCHEDULE_FAILED'
    ) {
        cardType = APPOINTMENT_CARD_TYPE.SELECT_SLOT;
    } else if (
        (status === 'PAYMENT_CREATED' || status === 'PAYMENT_FAILED') &&
        payment_details?.payin_url
    ) {
        cardType = APPOINTMENT_CARD_TYPE.COMPLETE_PAYMENT;
    } else if (status === 'PAYMENT_IN_PROGRESS') {
        cardType = APPOINTMENT_CARD_TYPE.CONTACT_US;
    }

    if (cardType && appointmentCardFields[cardType]) {
        return {
            ...appointmentCardFields[cardType](appointmentDetails),
            vehicleDetails: appointmentDetails.vehicle_details,
            customerDetails: appointmentDetails.customer_details,
            booking: appointmentDetails,
        };
    }

    // Default fallback
    return {
        ctaName: '',
        paymentLink: '',
        cardText: '',
        navigatePath: '',
        cardName: '',
        vehicleDetails: appointmentDetails.vehicle_details,
        customerDetails: appointmentDetails.customer_details,
        booking: appointmentDetails,
    };
}

export const { setAppointments, clearAppointments } = appointmentsSlice.actions;
export const selectAppointments = (state: any) =>
    state.appointmentData?.appointments;
export const selectCardDetails = (state: any) =>
    state.appointmentData.cardDetails;

export default appointmentsSlice.reducer;
